import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * Toasts and a confirm sheet.
 *
 * The browser dialogs are not options here: `alert` and `confirm` block the
 * event loop, which would freeze the live board mid-update, and they cannot be
 * styled or dismissed programmatically.
 */

export type Tone = 'ok' | 'error' | 'info';

/**
 * Which notifications a person wants to see.
 *
 * `all` is everything; `important` keeps errors and anything that offers a
 * next step; `errors` is errors alone. `muted` silences particular kinds of
 * message -- "Stop moved", say -- whatever the level. Errors always show: a
 * failure nobody sees is the one setting that would do real harm.
 */
export type NotificationLevel = 'all' | 'important' | 'errors';
export interface NotificationPrefs {
  level: NotificationLevel;
  muted: string[];
}
export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = { level: 'all', muted: [] };

/**
 * The kind a message belongs to: its title without the parts that change --
 * numbers and quoted names -- so "Moved 3 stops" and "Moved 12 stops" mute
 * together, and "Van 07 stayed put" with "Van 12 stayed put".
 */
export function notificationKind(title: string): string {
  return title
    .replace(/[“"‘][^”"’]*[”"’]/g, '…')
    .replace(/\d+/g, '#')
    .replace(/\s+/g, ' ')
    .trim();
}

interface Toast {
  id: number;
  tone: Tone;
  title: string;
  body?: string;
  /**
   * One thing to do about it, offered rather than done.
   *
   * Some changes only take effect after a further step -- moving a shift
   * constrains the *next* build, so the day on screen still shows the old one.
   * Saying so in words and leaving the operator to find the button was enough
   * for a real dispatcher to conclude the drag had done nothing.
   */
  action?: { label: string; run: () => void };
  kind: string;
}

interface ConfirmRequest {
  title: string;
  body: string;
  confirmLabel: string;
  /**
   * A second way to say yes.
   *
   * Some questions have two right answers and one of them is not "cancel":
   * replanning a day can keep the stops somebody placed by hand or reconsider
   * them, and forcing that into yes/no would make one of the two look like the
   * safe default when neither is.
   */
  altLabel: string | null;
  danger: boolean;
  resolve: (choice: 'confirm' | 'alt' | 'cancel') => void;
}

interface Ui {
  notify: (
    tone: Tone,
    title: string,
    body?: string,
    action?: { label: string; run: () => void },
  ) => void;
  confirm: (options: {
    title: string;
    body: string;
    confirmLabel?: string;
    danger?: boolean;
  }) => Promise<boolean>;
  /** A question with two answers and a way out. Returns what was pressed. */
  choose: (options: {
    title: string;
    body: string;
    confirmLabel: string;
    altLabel: string;
    danger?: boolean;
  }) => Promise<'confirm' | 'alt' | 'cancel'>;
  /** The notification preferences in force. */
  notificationPrefs: NotificationPrefs;
  /**
   * Hand the provider a person's saved preferences, how to save a change, and
   * where their settings live. Until this is called every notification shows
   * and the toasts offer no mute link.
   */
  configureNotifications: (options: {
    prefs: NotificationPrefs;
    save: (prefs: NotificationPrefs) => Promise<void>;
    openSettings?: () => void;
  }) => void;
  setNotificationPrefs: (prefs: NotificationPrefs) => Promise<void>;
}

const UiContext = createContext<Ui | null>(null);

export function useUi(): Ui {
  const ctx = useContext(UiContext);
  if (!ctx) throw new Error('useUi must be used inside <UiProvider>');
  return ctx;
}

let nextId = 1;

export function UiProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [request, setRequest] = useState<ConfirmRequest | null>(null);

  const [prefs, setPrefs] = useState<NotificationPrefs>(DEFAULT_NOTIFICATION_PREFS);
  /* Read inside notify through a ref, so notify keeps one identity for every caller. */
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const saveRef = useRef<((p: NotificationPrefs) => Promise<void>) | null>(null);
  const settingsRef = useRef<(() => void) | null>(null);
  const [configured, setConfigured] = useState(false);

  const notify = useCallback<Ui['notify']>((tone, title, body, action) => {
    const kind = notificationKind(title);
    if (tone !== 'error') {
      const p = prefsRef.current;
      if (p.level === 'errors') return;
      if (p.level === 'important' && !action) return;
      if (p.muted.includes(kind)) return;
    }
    const id = nextId++;
    setToasts((current) => [...current, { id, tone, title, body, action, kind }]);
    /*
     * Errors stay long enough to read and copy; successes get out of the way.
     * One carrying an action stays longer still -- an offer nobody had time to
     * take is the same as not offering.
     */
    const ttl = tone === 'error' ? 9000 : action ? 12000 : 4500;
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), ttl);
  }, []);

  const choose = useCallback<Ui['choose']>(
    (options) =>
      new Promise<'confirm' | 'alt' | 'cancel'>((resolve) => {
        setRequest({
          title: options.title,
          body: options.body,
          confirmLabel: options.confirmLabel,
          altLabel: options.altLabel || null,
          danger: options.danger ?? false,
          resolve,
        });
      }),
    [],
  );

  const confirm = useCallback<Ui['confirm']>(
    (options) =>
      choose({
        title: options.title,
        body: options.body,
        confirmLabel: options.confirmLabel ?? 'Confirm',
        altLabel: '',
        danger: options.danger,
      }).then((answer) => answer === 'confirm'),
    [choose],
  );

  const setNotificationPrefs = useCallback<Ui['setNotificationPrefs']>(async (next) => {
    setPrefs(next);
    prefsRef.current = next;
    await saveRef.current?.(next);
  }, []);

  const configureNotifications = useCallback<Ui['configureNotifications']>((options) => {
    setPrefs(options.prefs);
    prefsRef.current = options.prefs;
    saveRef.current = options.save;
    settingsRef.current = options.openSettings ?? null;
    setConfigured(true);
  }, []);

  /* "Mute these": silence this kind, and clear the ones already on screen. */
  const mute = (kind: string) => {
    const p = prefsRef.current;
    if (!p.muted.includes(kind)) void setNotificationPrefs({ ...p, muted: [...p.muted, kind] });
    setToasts((current) => current.filter((t) => t.kind !== kind));
  };

  const value = useMemo(
    () => ({ notify, confirm, choose, notificationPrefs: prefs, configureNotifications, setNotificationPrefs }),
    [notify, confirm, choose, prefs, configureNotifications, setNotificationPrefs],
  );

  const settle = (choice: 'confirm' | 'alt' | 'cancel') => {
    request?.resolve(choice);
    setRequest(null);
  };

  return (
    <UiContext.Provider value={value}>
      {children}

      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className="toast" data-tone={toast.tone}>
            <div>
              <b>{toast.title}</b>
              {toast.body ? <p>{toast.body}</p> : null}
              {/* Never on an error: those always show, so there is nothing to mute. */}
              {configured && toast.tone !== 'error' ? (
                <span className="toast-links">
                  <button type="button" onClick={() => mute(toast.kind)}>
                    Mute these
                  </button>
                  {settingsRef.current ? (
                    <button
                      type="button"
                      onClick={() => {
                        setToasts((current) => current.filter((t) => t.id !== toast.id));
                        settingsRef.current?.();
                      }}
                    >
                      Notification settings
                    </button>
                  ) : null}
                </span>
              ) : null}
            </div>
            {toast.action ? (
              <button
                type="button"
                className="btn btn-small toast-action"
                onClick={() => {
                  setToasts((current) => current.filter((t) => t.id !== toast.id));
                  toast.action?.run();
                }}
              >
                {toast.action.label}
              </button>
            ) : null}
            {/*
              * Dismissable by hand.
              *
              * They time out on their own, and one carrying an action waits
              * twelve seconds for an answer -- long enough that a run of quick
              * moves stacks four or five of them over the board somebody is
              * trying to read. This is how you get your screen back without
              * taking the offer.
              */}
            <button
              type="button"
              className="toast-close"
              aria-label="Dismiss"
              onClick={() => setToasts((current) => current.filter((t) => t.id !== toast.id))}
            >
              <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true">
                <path
                  d="M2.5 2.5l7 7M9.5 2.5l-7 7"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        ))}
      </div>

      {request ? (
        <>
          <div className="scrim" onClick={() => settle('cancel')} />
          <div className="sheet" role="alertdialog" aria-modal="true" aria-label={request.title}>
            <h3>{request.title}</h3>
            <p>{request.body}</p>
            <div className="sheet-actions">
              <button type="button" className="btn" onClick={() => settle('cancel')}>
                Cancel
              </button>
              {request.altLabel ? (
                <button type="button" className="btn" onClick={() => settle('alt')}>
                  {request.altLabel}
                </button>
              ) : null}
              <button type="button" className="btn btn-primary" onClick={() => settle('confirm')}>
                {request.confirmLabel}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </UiContext.Provider>
  );
}
